import categoryFiltersApi from '@api/categoryFilters';
import { buildFilterConfigs } from '@client/routes/fetch/citySearchPage/utils/build-filter-configs';
import { featureFlagsAtom } from '@feature-flags/atoms';
import { FEATURE_FLAGS } from '@feature-flags/constants';
import {
	CATEGORY_FILTER_ID,
	type FilterConfigs,
	UNIVERSAL_FILTER_NAMES,
	baseFilterConfigsAtom,
	categoryFilterSummariesAtom,
	filterConfigsAtom,
	filterValuesAtom,
	isAllCategoriesPageAtom,
	listingsPricingByCategoryAtom,
} from '@pages/VendorsSearch/containers/FilterPills/atoms';
import { PEACE_OF_MIND_EXCLUDED_CATEGORY_CODES } from '@redux/experiments/data/peaceOfMindExcludedCategoryCodes';
import { selectAssignment } from '@redux/experiments/selectors/index';
import { setCategoryFilters } from '@redux/search';
import isResponseError from '@utils/isResponseError';
import { Logger } from '@utils/logger/logger';
import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { CategoryCode } from 'types/Category';
import type { SearchFilters } from 'types/search';
import { categoryCodeAtom } from '../../../../../jotai/redux-select-atoms';

type SelectedCategory = {
	facetId: string;
	facetName?: string;
	code?: string;
};

/**
 * Watches the category filter value and dynamically loads category-specific
 * filter configs when exactly 1 category is selected on the all-categories page.
 *
 * - 1 category selected: fetches that category's filters via the API, merges
 *   them with the cross-category filter config, and updates filterConfigsAtom +
 *   Redux pill state so the sidebar shows the right filters.
 * - 0 or 2+ categories selected: resets filterConfigsAtom to the server-
 *   initialized base configs (universal filters only).
 *
 * Safe to call unconditionally — no-ops when baseFilterConfigs has not been
 * initialized.
 */
// Module-level ref survives component remounts — prevents duplicate
// categoryFiltersApi calls when the hook re-mounts with the same category.
let latestCategoryIdGlobal: string | null = null;

// Session cache of resolved filter summaries, keyed by categoryId. Persists
// across category switches (and remounts) for the lifetime of the module so
// switching back to a previously-loaded category is instant and skips the
// network call entirely.
const categoryFilterCache = new Map<string, SearchFilters>();

// Reset function for testing — clears module-level state between test runs
export const __resetCategoryFilterSync = () => {
	latestCategoryIdGlobal = null;
	categoryFilterCache.clear();
};

export const useCategoryFilterSync = () => {
	const filterValues = useAtomValue(filterValuesAtom);
	const setFilterConfigs = useSetAtom(filterConfigsAtom);
	const baseFilterConfigs = useAtomValue(baseFilterConfigsAtom);
	const featureFlags = useAtomValue(featureFlagsAtom);
	const visitorId = useSelector(
		(state: Redux.State) => state.visitor?.id ?? '',
	);
	const categoryCode = useAtomValue(categoryCodeAtom);
	const isAllCategoriesPage = useAtomValue(isAllCategoriesPageAtom);
	const listingsPricingByCategory = useAtomValue(listingsPricingByCategoryAtom);
	const categoryFilterSummaries = useAtomValue(categoryFilterSummariesAtom);
	const setFilterValues = useSetAtom(filterValuesAtom);
	const pomAssignment = useSelector((state: Redux.State) =>
		selectAssignment(state, ['pomDirectoryPageFilters']),
	);
	const dispatch = useDispatch();

	// Derive selected categories from filterValues so effects only re-run
	// when the category selection changes, not on every filter interaction.
	const selectedCategories = useMemo<SelectedCategory[]>(() => {
		const categoryFilterValue = filterValues.find(
			(fv) => fv.id === CATEGORY_FILTER_ID,
		);
		return Array.isArray(categoryFilterValue?.value)
			? (categoryFilterValue.value as SelectedCategory[])
			: [];
	}, [filterValues]);

	// Ref for values only needed inside the async API callback, not to
	// determine whether the effects should run. useRef() provides the
	// initial value for the first render; the assignment below keeps it
	// current on every subsequent render (standard "latest ref" pattern).
	const asyncDepsRef = useRef({
		categoryCode,
		featureFlags,
		visitorId,
		listingsPricingByCategory,
		pomAssignment,
	});
	asyncDepsRef.current = {
		categoryCode,
		featureFlags,
		visitorId,
		listingsPricingByCategory,
		pomAssignment,
	};

	// Read selectedCategories inside the pre-select effect without making it a
	// reactive dependency. We deliberately want the pre-select to NOT re-run
	// when the user manually clears the selection — only on mount/navigation.
	// This also handles React 18 StrictMode's double-mount: refs reset but the
	// Jotai store preserves the already-selected category, so the length > 0
	// guard below will correctly skip re-pre-selection on the second mount.
	const selectedCategoriesRef = useRef(selectedCategories);
	selectedCategoriesRef.current = selectedCategories;

	// Reset to universal filters when 0 or 2+ categories are selected.
	useEffect(() => {
		if (!baseFilterConfigs) return;
		if (selectedCategories.length === 1) return;

		// Reset when we previously had a category loaded, or when multiple
		// categories are selected on initial load (server may have loaded
		// filters for a single category). Skip only when 0 categories are
		// selected on initial render to avoid unnecessary dispatches.
		if (latestCategoryIdGlobal === null && selectedCategories.length === 0) {
			return;
		}

		latestCategoryIdGlobal = null;
		// Extract only the universal subset so that single-category pages
		// also reset to Distance / BOW / BA rather than their full filter set.
		const universalConfigs: FilterConfigs = Object.fromEntries(
			Object.entries(baseFilterConfigs).filter(
				([id, config]) =>
					id === CATEGORY_FILTER_ID || UNIVERSAL_FILTER_NAMES.has(config.name),
			),
		);
		setFilterConfigs(universalConfigs);
		dispatch(
			setCategoryFilters(
				universalConfigs,
				// Use empty code so receiveFilterPills builds pills from
				// filterConfigs instead of a category-specific dictionary.
				'' as CategoryCode,
			),
		);
	}, [selectedCategories, baseFilterConfigs, setFilterConfigs, dispatch]);

	// Fetch category-specific filters when exactly 1 category is selected.
	useEffect(() => {
		if (!baseFilterConfigs) return;
		if (selectedCategories.length !== 1) return;

		const [{ facetId: categoryId }] = selectedCategories;

		// Avoid re-fetching if already loaded for this category
		if (latestCategoryIdGlobal === categoryId) return;
		latestCategoryIdGlobal = categoryId;

		const categoryOption = baseFilterConfigs[
			CATEGORY_FILTER_ID
		]?.filterOptions.find((opt) => opt.id === categoryId);
		const categorySlug = categoryOption?.plural.slug ?? '';
		const selectedCategoryCode = (categoryOption?.code ??
			asyncDepsRef.current.categoryCode) as CategoryCode;
		const shouldAddPeaceOfMindFilter =
			asyncDepsRef.current.pomAssignment === 'variant1' &&
			!PEACE_OF_MIND_EXCLUDED_CATEGORY_CODES.has(selectedCategoryCode);

		const applyFilterSummary = (filterSummary: SearchFilters) => {
			const categoryPricingFlag =
				asyncDepsRef.current.listingsPricingByCategory[selectedCategoryCode] ??
				false;
			const builtConfigs = buildFilterConfigs(
				filterSummary,
				{
					...asyncDepsRef.current.featureFlags,
					[FEATURE_FLAGS.LISTINGS_PRICING]: categoryPricingFlag,
				},
				categorySlug,
				shouldAddPeaceOfMindFilter,
			);

			const mergedConfigs: FilterConfigs = {
				...builtConfigs,
				// Always preserve the cross-category filter config so the category
				// selector remains visible after switching to category-specific filters.
				[CATEGORY_FILTER_ID]: baseFilterConfigs[CATEGORY_FILTER_ID],
			};

			setFilterConfigs(mergedConfigs);
			dispatch(setCategoryFilters(mergedConfigs, selectedCategoryCode));
		};

		// Check the session cache first, falling back to the summaries for every
		// category that were shipped down during the initial SSR page load (from
		// request.server.app.categories.slugToInfo, which already holds full filter
		// data for every category in memory). A hit on either source — including
		// the very first time this category is selected in the session — skips the
		// categoryFiltersApi call entirely.
		const cachedFilterSummary =
			categoryFilterCache.get(categoryId) ??
			categoryFilterSummaries[categoryId];

		if (cachedFilterSummary) {
			categoryFilterCache.set(categoryId, cachedFilterSummary);
			applyFilterSummary(cachedFilterSummary);
			return;
		}

		categoryFiltersApi(categoryId, asyncDepsRef.current.visitorId)
			.then((response) => {
				// Discard if the user changed category selection while this was in flight
				if (latestCategoryIdGlobal !== categoryId) return;

				const data = response.data;
				if (isResponseError(data) || !data.data?.filterSummary) {
					// Clear so the same category can be retried on next selection
					latestCategoryIdGlobal = null;
					return;
				}

				const filterSummary = data.data.filterSummary;
				categoryFilterCache.set(categoryId, filterSummary);
				applyFilterSummary(filterSummary);
			})
			.catch((err: unknown) => {
				// Allow the same category to be retried on next selection
				latestCategoryIdGlobal = null;
				Logger.error({
					log_message: `Failed to fetch category filters for categoryId="${categoryId}" (slug="${categorySlug}", code="${selectedCategoryCode}")`,
					errorToLog: err instanceof Error ? err : new Error(String(err)),
					newRelic: true,
				});
			});
	}, [
		selectedCategories,
		baseFilterConfigs,
		setFilterConfigs,
		dispatch,
		categoryFilterSummaries,
	]);

	// Pre-select the category from the URL on initial load of a single-category
	// page. When entering /marketplace/wedding-reception-venues-austin-tx
	// no ?category= param exists so
	// filterValuesAtom has no category entry — or it may have a stale empty
	// entry left by prior navigation/query-string parsing (value: []). This
	// effect detects both cases and injects (or replaces) the matching option
	// by categoryCode.
	//
	// selectedCategories is intentionally read via ref (not listed as a dep)
	// so manually clearing the selection does NOT re-trigger this effect —
	// only mount/navigation-driven dep changes (isAllCategoriesPage, categoryCode,
	// baseFilterConfigs) can re-run it. This also correctly handles React 18
	// StrictMode's double-mount: refs reset but Jotai preserves the already-
	// selected category, so the length > 0 guard skips re-pre-selection.
	//
	// Placed AFTER the reset/fetch effects so on the first commit:
	//   - reset effect: ref=null + length=0 → skips reset (correct)
	//   - fetch effect: length≠1 → skips API call (correct)
	// On the re-render triggered by setFilterValues:
	//   - fetch effect: ref===categoryId → skips API call (no regression)
	useEffect(() => {
		if (!baseFilterConfigs) return;
		if (isAllCategoriesPage) return;
		// Skip if a category is already actively selected (length > 0). This covers:
		//   - Normal guard: user already picked a category manually.
		//   - StrictMode double-mount: Jotai preserved the pre-selected entry.
		// Note: a stale empty entry (value: []) has length 0 and falls through;
		// it will be replaced in-place by the setFilterValues call below.
		if (selectedCategoriesRef.current.length > 0) return;

		const option = baseFilterConfigs[CATEGORY_FILTER_ID]?.filterOptions.find(
			(opt) => opt.code === categoryCode,
		);
		if (!option) return;

		// Guard against stale atoms during route transitions: the component remounts
		// on the new route before isAllCategoriesPageAtom is updated by the data fetch.
		// Using the URL as ground truth avoids pre-selecting on the all-categories page.
		if (!window.location.pathname.includes(option.plural.slug)) return;

		latestCategoryIdGlobal = option.id;
		// Replace an existing (possibly stale/empty) category entry in-place, or
		// append if none exists. Replacing prevents duplicate category entries that
		// would cause downstream .find() to return the stale entry instead of the
		// freshly pre-selected one.
		setFilterValues((prev) => {
			const nextEntry = {
				id: CATEGORY_FILTER_ID,
				value: [{ facetId: option.id }],
			};

			const existingIndex = prev.findIndex(
				(fv) => fv.id === CATEGORY_FILTER_ID,
			);
			if (existingIndex === -1) return [...prev, nextEntry];

			const next = [...prev];
			next[existingIndex] = nextEntry;
			return next;
		});
	}, [baseFilterConfigs, isAllCategoriesPage, categoryCode, setFilterValues]);
};
